import { useEffect, useState } from 'react'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { Input } from '../../components/atoms/Input'
import { Modal } from '../../components/atoms/Modal'
import { Select } from '../../components/atoms/Select'
import { Field } from '../../components/molecules/Field'
import { STAFFDEFAULTPASSWORD, STAFFNAMEMAX } from '../../../constants/staff.constants'
import type {
  CreateStaffInput,
  StaffDto,
  UpdateStaffInput,
  UserTypeOption
} from '../../../viewmodels/useStaff'

/**
 * Create / Edit modal for /iam/staff.
 *
 * CREATE: fields = first_name, last_name, email, user_type_code.
 *   Password field is rendered as a READ-ONLY display showing
 *   STAFFDEFAULTPASSWORD — admin doesn't type it. The handler hashes
 *   the literal server-side; the staff member resets via the
 *   welcome email link.
 *
 * EDIT: only first_name + last_name editable. Email + user_type +
 *   status all rendered read-only — per Kiran's spec.
 */

interface Props {
  readonly open: boolean
  readonly mode: 'create' | 'edit'
  readonly initial: StaffDto | null
  readonly userTypeOptions: ReadonlyArray<UserTypeOption>
  readonly onClose: () => void
  readonly onSubmit: (
    input: CreateStaffInput | UpdateStaffInput
  ) => Promise<{ ok: true } | { ok: false; field?: string | null; message: string }>
}

interface FormState {
  first_name: string
  last_name: string
  email: string
  user_type_code: string
}

function buildInitialState(
  mode: 'create' | 'edit',
  initial: StaffDto | null,
  userTypeOptions: ReadonlyArray<UserTypeOption>
): FormState {
  if (mode === 'edit' && initial !== null) {
    return {
      first_name: initial.first_name ?? '',
      last_name: initial.last_name ?? '',
      email: initial.email,
      user_type_code: initial.user_type_code ?? ''
    }
  }
  return {
    first_name: '',
    last_name: '',
    email: '',
    user_type_code: userTypeOptions[0]?.code ?? ''
  }
}

function clientValidate(
  state: FormState,
  mode: 'create' | 'edit'
): { field: keyof FormState; message: string } | null {
  if (state.first_name.trim().length === 0) {
    return { field: 'first_name', message: 'First name is required' }
  }
  if (state.first_name.length > STAFFNAMEMAX) {
    return { field: 'first_name', message: `First name must be ≤ ${STAFFNAMEMAX} chars` }
  }
  if (state.last_name.trim().length === 0) {
    return { field: 'last_name', message: 'Last name is required' }
  }
  if (state.last_name.length > STAFFNAMEMAX) {
    return { field: 'last_name', message: `Last name must be ≤ ${STAFFNAMEMAX} chars` }
  }
  if (mode === 'create') {
    if (state.email.trim().length === 0 || !state.email.includes('@')) {
      return { field: 'email', message: 'Valid email is required' }
    }
    if (state.user_type_code.trim().length === 0) {
      return { field: 'user_type_code', message: 'User type is required' }
    }
  }
  return null
}

export function StaffFormModal({
  open,
  mode,
  initial,
  userTypeOptions,
  onClose,
  onSubmit
}: Props): JSX.Element {
  const [state, setState] = useState<FormState>(() => buildInitialState(mode, initial, userTypeOptions))
  const [errMsg, setErrMsg] = useState<string | null>(null)
  const [errField, setErrField] = useState<keyof FormState | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (open) {
      setState(buildInitialState(mode, initial, userTypeOptions))
      setErrMsg(null)
      setErrField(null)
    }
  }, [open, mode, initial, userTypeOptions])

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault()
    const clientErr = clientValidate(state, mode)
    if (clientErr !== null) {
      setErrField(clientErr.field)
      setErrMsg(clientErr.message)
      return
    }
    setSubmitting(true)
    setErrMsg(null)
    setErrField(null)
    const payload: CreateStaffInput | UpdateStaffInput =
      mode === 'create'
        ? {
            first_name: state.first_name.trim(),
            last_name: state.last_name.trim(),
            email: state.email.trim().toLowerCase(),
            user_type_code: state.user_type_code
          }
        : {
            first_name: state.first_name.trim(),
            last_name: state.last_name.trim()
          }
    const res = await onSubmit(payload)
    setSubmitting(false)
    if (res.ok) {
      onClose()
      return
    }
    setErrMsg(res.message)
    if (res.field !== null && res.field !== undefined) {
      setErrField(res.field as keyof FormState)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={mode === 'create' ? 'Add staff' : 'Edit staff'}>
      <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="First name" error={errField === 'first_name' ? errMsg : null}>
            <Input
              value={state.first_name}
              onChange={(e) => setState((s) => ({ ...s, first_name: e.target.value }))}
              maxLength={STAFFNAMEMAX}
              hasError={errField === 'first_name'}
              autoFocus
            />
          </Field>
          <Field label="Last name" error={errField === 'last_name' ? errMsg : null}>
            <Input
              value={state.last_name}
              onChange={(e) => setState((s) => ({ ...s, last_name: e.target.value }))}
              maxLength={STAFFNAMEMAX}
              hasError={errField === 'last_name'}
            />
          </Field>
        </div>

        <Field
          label="Email"
          error={errField === 'email' ? errMsg : null}
          {...(mode === 'edit' ? { helper: 'Email cannot be changed after creation.' } : {})}
        >
          <Input
            type="email"
            value={state.email}
            onChange={(e) => setState((s) => ({ ...s, email: e.target.value }))}
            disabled={mode === 'edit'}
            hasError={errField === 'email'}
          />
        </Field>

        <Field
          label="User type"
          error={errField === 'user_type_code' ? errMsg : null}
          helper={
            mode === 'edit'
              ? 'User type cannot be changed after creation.'
              : 'Picks the post-login landing page + permission scope.'
          }
        >
          <Select
            value={state.user_type_code}
            onChange={(e) => setState((s) => ({ ...s, user_type_code: e.target.value }))}
            disabled={mode === 'edit'}
            hasError={errField === 'user_type_code'}
          >
            {userTypeOptions.length === 0 ? (
              <option value="" disabled>— No eligible user types —</option>
            ) : null}
            {userTypeOptions.map((opt) => (
              <option key={opt.code} value={opt.code}>
                {opt.name}
              </option>
            ))}
          </Select>
        </Field>

        {mode === 'create' ? (
          <Field
            label="Initial password"
            error={null}
            helper="Fixed for every new staff. Sent in the welcome email so the staff member can set their own."
          >
            <Input
              value={STAFFDEFAULTPASSWORD}
              readOnly
              className="font-mono bg-parchment/50 cursor-not-allowed"
            />
          </Field>
        ) : null}

        {errMsg !== null && errField === null ? (
          <Alert tone="error">{errMsg}</Alert>
        ) : null}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : mode === 'create' ? 'Add staff' : 'Save'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

